/**
 * Audit Logging System
 * 
 * This module provides functions for recording security-relevant events
 * to a centralized audit log stored in Supabase. This logging system
 * helps with security monitoring, incident response, and compliance.
 */

import { supabase } from '@/integrations/supabase/client';
import { SessionManager } from './session-service';
import { QuickLoggerFactory } from '@/lib/universal-logger';

// Initialize security namespace logger for audit system operations
const securityLogger = QuickLoggerFactory.security();

/**
 * Audit event severity levels
 */
export enum AuditLogSeverity {
  INFO = 'info',        // Normal operations, successful actions
  WARNING = 'warning',  // Potential issues or warnings that need attention
  ERROR = 'error',      // Failures or security issues
  CRITICAL = 'critical' // Severe security incidents requiring immediate attention
}

/**
 * Audit event categories
 */
export enum AuditLogCategory {
  AUTHENTICATION = 'authentication', // Login, logout, signup, password reset
  AUTHORIZATION = 'authorization',   // Permission checks, access control
  DATA_ACCESS = 'data_access',       // Reading/writing sensitive data
  ADMIN = 'admin',                   // Administrative actions
  API_KEY = 'api_key',               // API key management
  SYSTEM = 'system',                 // System-level events
  LLM_REQUEST = 'llm_request'        // LLM API requests
}

/**
 * Audit log event interface
 */
interface AuditLogEvent {
  event: string;                // Event name/description
  category: AuditLogCategory;   // Event category
  severity: AuditLogSeverity;   // Event severity
  user_id?: string;             // User ID if available
  ip_address?: string;          // IP address if available
  details: Record<string, any>; // Additional event details
  created_at?: string;          // Timestamp (set by database)
}

/**
 * Mask sensitive data in audit logs
 * 
 * @param value The value to potentially mask
 * @param key The key name to check if it should be masked
 * @returns Masked or original value
 */
function maskSensitiveData(value: any, key: string): any {
  // Keys that should be masked (passwords, tokens, keys, etc.)
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'apiKey', 'api_key',
    'authorization', 'auth', 'credentials', 'private'
  ];
  
  // Check if this is a sensitive key that should be masked
  const isSensitive = sensitiveKeys.some(sensitiveKey => 
    key.toLowerCase().includes(sensitiveKey.toLowerCase())
  );
  
  // If sensitive and is a string, mask it
  if (isSensitive && typeof value === 'string') {
    if (value.length <= 8) {
      return '******';
    }
    // For longer values, show first and last 2 characters
    return `${value.substring(0, 2)}******${value.substring(value.length - 2)}`;
  }
  
  // For non-sensitive or non-string values, return as is
  return value;
}

/**
 * Sanitize event details for storage
 * This prevents sensitive data from being logged
 * 
 * @param details Object with event details
 * @returns Sanitized details object
 */
function sanitizeDetails(details: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  
  // Process each key in the details object
  Object.entries(details).forEach(([key, value]) => {
    // Handle nested objects
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeDetails(value);
    } 
    // Handle arrays (don't process them recursively for simplicity)
    else if (Array.isArray(value)) {
      sanitized[key] = value;
    } 
    // Handle primitive values
    else {
      sanitized[key] = maskSensitiveData(value, key);
    }
  });
  
  return sanitized;
}

/**
 * Main function to log an audit event
 * 
 * @param event Event name/description
 * @param category Event category
 * @param severity Event severity
 * @param details Additional event details
 * @param userId Optional user ID (will be auto-detected if not provided)
 * @returns Promise resolving to success status
 */
export async function logAuditEvent(
  event: string,
  category: AuditLogCategory,
  severity: AuditLogSeverity = AuditLogSeverity.INFO,
  details: Record<string, any> = {},
  userId?: string
): Promise<boolean> {
  try {
    // Try to get the current user ID if not provided
    // Handle this gracefully for anonymous users
    if (!userId) {
      try {
        const currentUser = await SessionManager.getCurrentUser();
        userId = currentUser?.id;
      } catch (sessionError) {
        // Gracefully handle the case where there's no session (anonymous users)
        // Don't log this as an error since it's expected for anonymous operations
        securityLogger.debug('No user session available for audit logging (anonymous user)', {
          operation: 'logAuditEvent',
          event,
          category,
          severity
        });
        userId = undefined; // Explicitly set to undefined for anonymous logs
      }
    }
    
    // Sanitize the details before logging
    const sanitizedDetails = sanitizeDetails(details);
    
    // Create the audit log entry
    const logEntry: AuditLogEvent = {
      event,
      category,
      severity,
      user_id: userId,
      details: sanitizedDetails
    };
    
    // Insert into the audit_log table - note that this assumes 
    // the table exists (will need to be created via migration)
    const { error } = await supabase.from('audit_log').insert(logEntry);
    
    // Log successful audit events for security monitoring
    if (!error) {
      securityLogger.info('Audit event logged successfully', {
        operation: 'logAuditEvent',
        event,
        category,
        severity,
        userId,
        hasDetails: Object.keys(sanitizedDetails).length > 0
      });
    }
    
    if (error) {
      // Don't spam the console if the table doesn't exist (404)
      if (error.message?.includes('404') || error.message?.includes('not found')) {
        // Keep console.warn for immediate admin visibility, but also log through Universal Logger
        console.warn('Audit log table not found. Create the audit_log table in Supabase to enable audit logging.');
        securityLogger.warn('Audit log table not found', {
          operation: 'logAuditEvent',
          event,
          category,
          severity,
          userId,
          tableStatus: 'missing'
        });
      } else {
        // Keep console.error for immediate admin visibility, but also log through Universal Logger
        console.error('Error logging audit event:', error);
        securityLogger.error('Database error during audit logging', {
          operation: 'logAuditEvent',
          event,
          category,
          severity,
          userId,
          error: {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          }
        });
      }
      return false;
    }
    
    return true;
  } catch (err) {
    // Keep console.error for immediate admin visibility, but also log through Universal Logger
    console.error('Unexpected error in audit logging:', err);
    securityLogger.error('Unexpected error in audit logging system', {
      operation: 'logAuditEvent',
      event,
      category,
      severity,
      userId,
      error: {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined
      }
    });
    return false;
  }
}

/**
 * Log an authentication event
 * 
 * @param event Event name (e.g., "login_success", "login_failed")
 * @param details Event details
 * @param severity Event severity
 * @param userId User ID if available
 * @returns Promise resolving to success status
 */
export async function logAuthEvent(
  event: string,
  details: Record<string, any> = {},
  severity: AuditLogSeverity = AuditLogSeverity.INFO,
  userId?: string
): Promise<boolean> {
  securityLogger.debug('Authentication audit event initiated', {
    operation: 'logAuthEvent',
    event,
    severity,
    userId,
    hasDetails: Object.keys(details).length > 0
  });
  return logAuditEvent(event, AuditLogCategory.AUTHENTICATION, severity, details, userId);
}

/**
 * Log an API key management event
 * 
 * @param event Event name (e.g., "api_key_added", "api_key_deleted")
 * @param details Event details
 * @param severity Event severity
 * @param userId User ID if available
 * @returns Promise resolving to success status
 */
export async function logApiKeyEvent(
  event: string,
  details: Record<string, any> = {},
  severity: AuditLogSeverity = AuditLogSeverity.INFO,
  userId?: string
): Promise<boolean> {
  securityLogger.debug('API key audit event initiated', {
    operation: 'logApiKeyEvent',
    event,
    severity,
    userId,
    hasDetails: Object.keys(details).length > 0
  });
  return logAuditEvent(event, AuditLogCategory.API_KEY, severity, details, userId);
}

/**
 * Log an LLM request event
 * 
 * @param event Event name (e.g., "llm_request_sent", "llm_response_received")
 * @param details Event details
 * @param severity Event severity
 * @param userId User ID if available
 * @returns Promise resolving to success status
 */
export async function logLlmRequestEvent(
  event: string,
  details: Record<string, any> = {},
  severity: AuditLogSeverity = AuditLogSeverity.INFO,
  userId?: string
): Promise<boolean> {
  securityLogger.debug('LLM request audit event initiated', {
    operation: 'logLlmRequestEvent',
    event,
    severity,
    userId,
    hasDetails: Object.keys(details).length > 0
  });
  return logAuditEvent(event, AuditLogCategory.LLM_REQUEST, severity, details, userId);
}

/**
 * Create SQL migration for the audit_log table
 * This is a utility function to generate the SQL needed to
 * create the audit_log table in Supabase
 * 
 * @returns SQL string for creating the audit_log table
 */
export function generateAuditLogTableSQL(): string {
  return `
-- Create audit log table
CREATE TABLE IF NOT EXISTS public.audit_log (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    event TEXT NOT NULL,
    category TEXT NOT NULL,
    severity TEXT NOT NULL,
    user_id UUID REFERENCES auth.users(id),
    ip_address TEXT,
    details JSONB NOT NULL DEFAULT '{}'::jsonb,
    created_at TIMESTAMPTZ NOT NULL DEFAULT now()
);

-- Add RLS policies
ALTER TABLE public.audit_log ENABLE ROW LEVEL SECURITY;

-- Only allow reading own logs for regular users
CREATE POLICY "Users can view their own audit logs"
  ON public.audit_log
  FOR SELECT
  USING (auth.uid() = user_id);

-- Only allow admins to view all logs
CREATE POLICY "Admins can view all audit logs"
  ON public.audit_log
  FOR SELECT
  USING (
    EXISTS (
      SELECT 1 FROM auth.users
      WHERE auth.users.id = auth.uid()
      AND auth.users.role = 'admin'
    )
  );

-- Create index on user_id and created_at for faster lookups
CREATE INDEX IF NOT EXISTS audit_log_user_id_idx ON public.audit_log (user_id);
CREATE INDEX IF NOT EXISTS audit_log_created_at_idx ON public.audit_log (created_at);
CREATE INDEX IF NOT EXISTS audit_log_category_idx ON public.audit_log (category);
CREATE INDEX IF NOT EXISTS audit_log_severity_idx ON public.audit_log (severity);
  `;
}

/**
 * Usage examples:
 * 
 * // Log a successful login
 * await logAuthEvent('login_success', { method: 'password' });
 * 
 * // Log a failed login attempt
 * await logAuthEvent('login_failed', { 
 *   reason: 'invalid_password',
 *   email: 'user@example.com' // Email will be sanitized in the logs
 * }, AuditLogSeverity.WARNING);
 * 
 * // Log API key operations
 * await logApiKeyEvent('api_key_added', { provider: 'openai' });
 * 
 * // Log LLM requests
 * await logLlmRequestEvent('llm_request', { 
 *   provider: 'openai',
 *   model: 'gpt-4',
 *   prompt_length: 250,
 *   completion_length: 1024
 * });
 */